<template>
	<ais-instant-search
		:search-client="searchClient"
		:routing="routing"
		:index-name="searchIndex"
		class="c-site-search o-grid ais-index"
	>
		<ais-configure :filters="customFilters"></ais-configure>

		<div class="o-grid__item c-site-search__search-box">
			<ais-stats>
				<template v-slot="{ query }">
					<h2 v-if="query.length > 0 && query !== '*'" class="c-site-search__heading">
						Results for <span class="h-accessibility">{{ query }}</span>
					</h2>
					<h2 v-else class="c-site-search__heading">
						Search <span class="h-accessibility">form</span>
					</h2>
				</template>
			</ais-stats>

			<ais-search-box>
				<template v-slot="{ currentRefinement, isSearchStalled, refine }">
					<div class="c-form-field--site-search">
						<label class="h-accessibility"> Search </label>
						<div class="c-form-field__inputset">
							<div class="c-form-field__input">
								<custom-ais-input></custom-ais-input>
								<span class="c-form-field__append icon" aria-hidden="true">
									<searchIcon />
								</span>
							</div>
						</div>
					</div>
				</template>
			</ais-search-box>

			<ais-stats class="c-site-search__count">
				<template v-slot="{ nbHits, query}">
					<span v-if="query.length > 0 && query !== '*'">
						{{ nbHits }} found
					</span>
					<span v-else></span>
				</template>
			</ais-stats>
		</div>

		<ais-stats class="o-grid__item c-site-search__prompt">
			<template v-slot="{ query }">
				<div v-if="query.length > 0 && query !== '*'" class="c-site-search__prompt-inner">
					<span class="icon c-site-search__prompt-icon" aria-hidden="true"><infoIcon /></span>
					<p class="h-text--link c-site-search__prompt-text">
						Looking for something in our collections? <a :href="collectionsUrl(query)">Search our collections for "{{ query }}"</a> instead.
					</p>
				</div>
				<div v-else></div>
			</template>
		</ais-stats>

		<ais-state-results class="o-grid__item">
			<template v-slot="{ results: { hits, query } }">
				<ais-hits
					v-if="hits.length > 0 && query.length > 0 && query !== '*'"
					:class-names="{
						'ais-Hits-list': 'o-list c-site-search__list',
						'ais-Hits-item': 'c-site-search__item',
					}"
				>
					<template v-slot:item="{ item }">
						<a :href="item.uri" class="c-site-search__result">
							<div class="c-site-search__image" v-if="item.searchImage">
								<div class="o-image">
									<img class="o-image__img" :src="item.searchImage" alt="">
								</div>
							</div>

							<div class="c-site-search__text">
								<h3 class="c-site-search__title" v-html="item.title"></h3>
								<p class="c-site-search__type" v-html="item.typeDisplay"></p>
								<p
									v-if="item.dateString"
									class="c-site-search__date"
									v-html="item.dateString"
								></p>
								<p class="c-site-search__summary" v-html="item.summary"></p>
							</div>
						</a>
					</template>
				</ais-hits>

				<div v-else-if="query.length > 0 && query !== '*'" class="o-block">
					No results have been found for <span v-text="`&quot;${query}&quot;`"></span>.
				</div>

				 <!-- empty div necessary to stop component instructions rendering -->
				 <div v-else></div>

				<div class="o-grid__item o-block" v-if="hits.length > 0 && query !== '*'">
					<nav class="c-pagination" aria-label="Pagination">
						<ais-pagination
							v-on:page-change="scrollTop"
							:padding="2"
						>
							<template
								v-slot="{
									currentRefinement,
									nbPages,
									pages,
									isFirstPage,
									isLastPage,
									refine,
									createURL,
								}"
							>
								<ul class="c-pagination__list">
									<li class="c-pagination__item c-pagination__first">
										<a
											class="c-pagination__link"
											:class="{ 'is-disabled': isFirstPage }"
											:href="createURL(0)"
											@click.prevent="refine(0)"
										>
											First
										</a>
									</li>
									<li class="c-pagination__item c-pagination__prev">
										<a
											:href="createURL(currentRefinement - 1)"
											@click.prevent="refine(currentRefinement - 1)"
											class="c-pagination__link"
											:class="{ 'is-disabled': isFirstPage }"
											aria-label="Previous"
										>
											<span class="icon">
												<chevronLeft />
											</span>
										</a>
									</li>
									<li
										class="c-pagination__item"
										:class="[page == currentRefinement ? 'c-pagination__current' : '']"
										v-for="page in pages"
										:key="page"
									>
										<a
											class="c-pagination__link"
											:class="[page == currentRefinement ? 'is-active' : '']"
											:href="createURL(page)"
											@click.prevent="refine(page)"
											v-html="page + 1"
										>
										</a>
									</li>
									<li class="c-pagination__item c-pagination__next">
										<a
											:href="createURL(currentRefinement + 1)"
											@click.prevent="refine(currentRefinement + 1)"
											class="c-pagination__link"
											:class="{ 'is-disabled': isLastPage }"
											aria-label="Next"
										>
											<span class="icon">
												<chevronRight />
											</span>
										</a>
									</li>
									<li class="c-pagination__item c-pagination__last">
										<a
											class="c-pagination__link"
											:class="{ 'is-disabled': isLastPage }"
											:href="createURL(nbPages)"
											@click.prevent="refine(nbPages)"
										>
											Last
										</a>
									</li>
								</ul>
							</template>
						</ais-pagination>
					</nav>
				</div>
			</template>
		</ais-state-results>
	</ais-instant-search>
</template>

<script>
	import { history } from "instantsearch.js/es/lib/routers";
	import { singleIndex as singleIndexMapping } from "instantsearch.js/es/lib/stateMappings";
	import {
		AisInstantSearch,
		AisConfigure,
		AisClearRefinements,
		AisCurrentRefinements,
		AisRefinementList,
		AisHits,
		AisPagination,
		AisPoweredBy,
		AisSearchBox,
		AisStateResults,
		AisStats
	} from "vue-instantsearch/vue3/es";
	import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";

	import customAisInput from "./algolia/custom-ais-input.vue";

	import chevronRight from '/web/static/images/svgs/icon-chevron-right.svg?component';
	import chevronLeft  from '/web/static/images/svgs/icon-chevron-left.svg?component';
	import searchIcon from "/web/static/images/svgs/icon-search.svg?component";
	import infoIcon from "/web/static/images/svgs/icon-info.svg?component";

	export default {
		data() {
			return {
				routing: {
					router: history(),
					stateMapping: singleIndexMapping(this.$props.searchIndex),
				},
				searchClient: this.searchClient,
			};
		},
		props: {
			searchKey: {
				type: String,
			},
			searchNodes: {
				type: Array,
			},
			searchPort: {
				type: String,
			},
			searchProtocol: {
				type: String,
			},
			searchIndex: {
				type: String,
			},
		},
		created() {
			const splitNodes = this.$props.searchNodes.split(";");

			const mappedNodes = splitNodes.map((node) => ({
				host: node,
				port: this.$props.searchPort,
				protocol: this.$props.searchProtocol,
			}));

			const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
				server: {
					apiKey: this.$props.searchKey, // Be sure to use an API key that only allows search operations
					nodes: mappedNodes,
				},
				additionalSearchParameters: {
					query_by: "title,summary,eventPriceNotes",
				},
			});

			this.searchClient = typesenseInstantsearchAdapter.searchClient;
		},
		computed: {
			customFilters: function () {
				let now = Math.floor(new Date().getTime() / 1000);
				// If an entry has a searchExpiryDate, then only return if in the future
				// see https://www.algolia.com/doc/api-reference/api-parameters/facetFilters/#usage-notes
				return [`searchExpiryDate: [0, >=${now}]`], `hideFromSearch:!=true`;
			},
		},
		methods: {
			collectionsUrl(q) {
				return `/search-our-collections?description=${ q }#search`
			},
			// placing function here as inline was throwing 'window not defined' error
			scrollTop() {
				window.scrollTo(0,0);
			},
		},
		components: {
			AisInstantSearch,
			AisConfigure,
			AisClearRefinements,
			AisCurrentRefinements,
			AisRefinementList,
			AisHits,
			AisPagination,
			AisPoweredBy,
			AisSearchBox,
			AisStateResults,
			customAisInput,
			searchIcon,
			chevronRight,
			chevronLeft,
			infoIcon,
			AisStats
		},
	};
</script>
